import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Email verification state (after login, before entering app)
  const [needVerify, setNeedVerify] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("請輸入信箱與密碼");
      return;
    }

    setIsLoading(true);

    // Safety: auto-release loading after 15 seconds no matter what
    const safetyTimer = setTimeout(() => {
      setIsLoading(false);
      setError("登入逾時，請檢查網路後重新嘗試");
    }, 15000);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      clearTimeout(safetyTimer);

      if (authError) {
        setIsLoading(false);
        setError(
          authError.message === "Invalid login credentials"
            ? "信箱或密碼錯誤，請重新輸入"
            : authError.message,
        );
        return;
      }

      // Check if email verification is needed (non-blocking - don't keep loading)
      setIsLoading(false);

      const { data: emp } = await supabase
        .from("employees")
        .select("role_level, email_verified")
        .ilike("email", email.trim())
        .maybeSingle();

      // Admin skips verification; verified users proceed directly
      if (emp?.role_level === "admin" || emp?.email_verified) {
        navigate("/");
        return;
      }

      // Needs email verification
      setVerifyEmail(email.trim());
      setNeedVerify(true);
      setVerifyCode("");
      setVerifyError("");
      setCodeSent(false);
    } catch (err: any) {
      clearTimeout(safetyTimer);
      setIsLoading(false);
      setError(err?.message || "登入失敗，請稍後再試");
    }
  };

  const handleSendCode = async () => {
    if (!verifyEmail) return;
    setSendingCode(true);
    setVerifyError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setVerifyError("請先登入");
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ""}/api/functions/send-verification-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: verifyEmail, senderEmail: "admin1356@magic-happens.top" }),
        }
      );
      const result = await resp.json();

      if (resp.ok && result.success) {
        setCodeSent(true);
      } else {
        setVerifyError(result.message || "發送失敗，請稍後再試");
      }
    } catch {
      setVerifyError("發送失敗，請稍後再試");
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyCode.trim() || verifyCode.trim().length !== 6) {
      setVerifyError("請輸入 6 位數驗證碼");
      return;
    }

    setVerifyingCode(true);
    setVerifyError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setVerifyError("請先登入");
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || ""}/api/functions/verify-email-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: verifyEmail, code: verifyCode.trim() }),
        }
      );
      const result = await resp.json();

      if (resp.ok && result.success) {
        navigate("/");
      } else {
        setVerifyError(result.message || "驗證失敗，請確認驗證碼是否正確");
      }
    } catch {
      setVerifyError("驗證失敗，請稍後再試");
    } finally {
      setVerifyingCode(false);
    }
  };

  // ===== VERIFICATION VIEW =====
  if (needVerify) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url(/login-bg.png)",
          }}
        />
        <div className="absolute inset-0 bg-black/40" />

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="ri-goblet-fill text-background-50 text-xl" />
            </div>
            <h1
              className="text-2xl font-bold text-white mb-1 drop-shadow"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              酒藝商貿有限公司
            </h1>
            <p className="text-sm text-white/70 drop-shadow">
              紅酒批發營運管理平台
            </p>
          </div>

          <div className="bg-background-50/95 backdrop-blur-sm border border-background-200/50 rounded-2xl p-6 md:p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-mail-check-line text-primary-500 text-xl" />
              </div>
              <h2 className="text-lg font-semibold text-foreground-900">
                首次登入 - 信箱驗證
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                請驗證 <strong className="text-foreground-700">{verifyEmail}</strong> 以確保帳號安全
              </p>
            </div>

            {verifyError && (
              <div className="flex items-center gap-2 text-sm text-accent-600 bg-accent-50 px-3 py-2 rounded-lg mb-4">
                <i className="ri-error-warning-line" />
                {verifyError}
              </div>
            )}

            {!codeSent ? (
              <button
                onClick={handleSendCode}
                disabled={sendingCode}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {sendingCode ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    發送中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    發送驗證碼
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-foreground-500 text-center">
                  驗證碼已發送至您的信箱，請在 10 分鐘內輸入
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => {
                      setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setVerifyError("");
                    }}
                    placeholder="輸入6位驗證碼"
                    maxLength={6}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50 tracking-[6px] text-center text-lg"
                  />
                  <button
                    onClick={handleVerifyCode}
                    disabled={verifyingCode || verifyCode.trim().length !== 6}
                    className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                  >
                    {verifyingCode ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-loader-4-line animate-spin" />
                        驗證中
                      </span>
                    ) : (
                      "確認"
                    )}
                  </button>
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={sendingCode}
                  className="w-full text-xs text-primary-600 hover:text-primary-700 cursor-pointer disabled:opacity-50 underline"
                >
                  {sendingCode ? "重新發送中..." : "重新發送驗證碼"}
                </button>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-background-200">
              <p className="text-xs text-foreground-500 text-center">
                如需協助，請聯繫總管理員
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== LOGIN VIEW =====
  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url(/login-bg.png)",
        }}
      />
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/40" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <i className="ri-goblet-fill text-background-50 text-xl" />
          </div>
          <h1
            className="text-2xl font-bold text-white mb-1 drop-shadow"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            酒藝商貿有限公司
          </h1>
          <p className="text-sm text-white/70 drop-shadow">
            紅酒批發營運管理平台
          </p>
        </div>

        <div className="bg-background-50/95 backdrop-blur-sm border border-background-200/50 rounded-2xl p-6 md:p-8 shadow-xl">
          <h2 className="text-lg font-semibold text-foreground-900 mb-6">
            登入系統
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                電子信箱
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-foreground-400 text-sm" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="請輸入信箱"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                密碼
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-foreground-400 text-sm" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="請輸入密碼"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-accent-600 bg-accent-50 px-3 py-2 rounded-lg">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  登入中...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  登入
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-background-200">
            <p className="text-xs text-foreground-500 text-center">
              如需開通帳號，請聯繫總管理員
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}